import { Suspense, useCallback, useEffect, useState } from 'react';
import { useLocation } from 'react-router-dom';
import useAuth from '../../hooks/useAuth';
import api from '../../utils/api';
import lazyChunk from '../../utils/lazyChunk';

// Never pop the review prompt over auth / registration surfaces — an existing
// client signing in with Google mid-salon-registration (/pro/register step 1)
// would otherwise get a "lasă o recenzie" modal over "name your salon".
const SKIP_PATH_PREFIXES = ['/login', '/register', '/pro/', '/forgot-password', '/reset-password', '/verify-email', '/accept-invite'];

// Lazy — the modal pulls in toast + extra icons and is shown to at most
// a small subset of users on any given page load. Keep it out of the
// main bundle until we know there's actually a pending review.
const ReviewPromptModal = lazyChunk(() => import('./ReviewPromptModal'));

// Per-user 24h cooldown — survives reload, scoped to user.id so a shared
// device doesn't accidentally suppress one user's prompt because another
// user dismissed theirs. localStorage is best-effort: SSR / private-mode
// / extension-blocked storage all silently fall back to "no cooldown",
// which means worst case we ignore the cooldown for that session — much
// better than letting a thrown exception break auth-resolved mounts.
const COOLDOWN_MS = 24 * 60 * 60 * 1000;
const cooldownKey = (userId) => `petzy_review_prompt_cooldown_${userId}`;

function isCooldownActive(userId) {
  if (!userId) return false;
  try {
    const ts = localStorage.getItem(cooldownKey(userId));
    if (!ts) return false;
    const age = Date.now() - Number(ts);
    return Number.isFinite(age) && age >= 0 && age < COOLDOWN_MS;
  } catch {
    return false;
  }
}

function markCooldown(userId) {
  if (!userId) return;
  try {
    localStorage.setItem(cooldownKey(userId), String(Date.now()));
  } catch {
    /* private mode / storage disabled — silently skip */
  }
}

// One-shot fetch on auth-resolved mount. Hits /bookings/pending-review,
// which returns the most-recent COMPLETED booking whose review row
// doesn't exist and whose reviewPromptDismissed flag is still false.
//
// Runs for ANY authenticated user. Any role can have client-side bookings
// (a specialist / salon owner who booked grooming for their own pet), so we
// no longer gate on role === CLIENT — the endpoint scopes by clientId ===
// req.user.id, so a user with no completed client bookings just gets null.
//
// Cadence layers (most restrictive wins):
//   1. Per-render `closed` flag — set on modal close. Blocks the effect
//      from re-firing in the same React tree even if user.id flips and
//      back inside the session.
//   2. Per-user localStorage cooldown — set on modal close, expires in
//      COOLDOWN_MS. Survives reloads. Prevents the "5 unreviewed
//      bookings = 5 reload-spawned modals" UX from Gemini's audit.
//   3. Server-side `reviewPromptDismissed` + `review: null` — the
//      booking itself stops surfacing in /pending-review once
//      resolved, so the cooldown is belt-and-braces for OTHER
//      eligible bookings the user has.
export default function GlobalReviewChecker() {
  const { user, loading } = useAuth();
  const { pathname } = useLocation();
  const [pending, setPending] = useState(null);
  const [closed, setClosed] = useState(false);
  const onSkippedPath = SKIP_PATH_PREFIXES.some((p) => pathname.startsWith(p));

  useEffect(() => {
    if (loading) return;
    if (!user?.id) return;
    if (closed) return;
    if (onSkippedPath) return;
    if (isCooldownActive(user.id)) return;

    let cancelled = false;
    api.get('/bookings/pending-review')
      .then(({ data }) => {
        if (!cancelled && data?.booking) setPending(data.booking);
      })
      .catch((err) => {
        // Silent — a transient 5xx here shouldn't disrupt the app.
        // Same booking will re-surface on the next session if eligible.
        console.warn('Pending-review lookup failed:', err?.message || err);
      });

    return () => { cancelled = true; };
  }, [user?.id, user?.role, loading, closed, onSkippedPath]);

  // useCallback so the child modal's useCallback over handleDismiss
  // actually stays stable — otherwise this parent re-renders for any
  // reason and recreates handleClose, which recreates handleDismiss,
  // which re-attaches the modal's Escape keydown listener every render.
  // Only depends on user?.id (markCooldown looks it up at call time);
  // setPending / setClosed are React's stable useState setters.
  const handleClose = useCallback(() => {
    // Cooldown set BEFORE clearing pending — if the user has 5
    // unreviewed bookings and immediately reloads, the next mount
    // hits the cooldown branch and skips the fetch entirely. Set
    // whether the user submitted OR dismissed: in both cases they've
    // signalled they're done with the prompt for now.
    markCooldown(user?.id);
    setPending(null);
    setClosed(true);
  }, [user?.id]);

  if (!pending) return null;

  return (
    <Suspense fallback={null}>
      <ReviewPromptModal booking={pending} onClose={handleClose} />
    </Suspense>
  );
}
